"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Crown, Trophy, Users } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { arenaApi } from "@/shared/api/arena";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Badge } from "@/shared/ui/data-display/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { cn } from "@/shared/lib/utils";
import {
  TournamentPodium,
  type PodiumEntry,
} from "@/widgets/tournament-detail/tournament-podium";
import { ArenaStandingsTable } from "@/widgets/tournament-detail/arena/arena-standings-table";
import { TournamentSoloParticipantsGrid } from "@/widgets/tournament-detail/tournament-solo-participants-grid";

type TeamMember = {
  userId?: string | null;
  displayName: string;
  avatarUrl?: string | null;
  isCaptain?: boolean;
};

type TeamCardData = {
  key: string;
  rosterId?: string | null;
  rank?: number;
  name: string;
  tag?: string | null;
  points?: number;
  lobbyNumber?: number | null;
  members: TeamMember[];
};

const RANK_ACCENT: Record<number, string> = {
  1: "border-amber-400/45 text-amber-400",
  2: "border-slate-300/40 text-slate-300",
  3: "border-orange-500/40 text-orange-400",
};

/** Карточка итогового состава одной команды: место (если есть), название, Σ баллов и игроки. */
function ArenaTeamRosterCard({
  team,
  showLobby,
}: {
  team: TeamCardData;
  showLobby: boolean;
}) {
  const tCommon = useTranslations("Common");
  const tUi = useTranslations("Ui");
  const accent =
    (team.rank && RANK_ACCENT[team.rank]) || "border-border text-muted-foreground";
  const nameEl = team.rosterId ? (
    <Link
      href={`/rosters/${team.rosterId}`}
      className="truncate font-semibold leading-tight hover:underline"
    >
      {team.name}
    </Link>
  ) : (
    <span className="truncate font-semibold leading-tight">{team.name}</span>
  );
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-black/6 p-4 dark:bg-white/4">
      <div className="flex items-center gap-2">
        {team.rank ? (
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold tabular-nums",
              accent,
            )}
          >
            {team.rank}
          </span>
        ) : null}
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5">
            {nameEl}
            {team.tag ? (
              <span className="shrink-0 font-mono text-xs text-muted-foreground">
                {team.tag}
              </span>
            ) : null}
          </span>
          {showLobby && team.lobbyNumber ? (
            <span className="text-xs text-muted-foreground">Лобби {team.lobbyNumber}</span>
          ) : null}
        </span>
        {team.points !== undefined ? (
          <Badge variant="secondary" className="shrink-0 tabular-nums">
            {team.points}&nbsp;{tCommon("pointsShort")}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        {team.members.map((m, i) => {
          const inner = (
            <>
              <Avatar className="h-6 w-6">
                <AvatarImage src={m.avatarUrl ?? undefined} alt={m.displayName} />
                <AvatarFallback className="text-[10px]">
                  {(m.displayName || "?").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-sm">{m.displayName}</span>
              {m.isCaptain ? (
                <Crown className="h-3.5 w-3.5 shrink-0 text-amber-400" aria-label={tUi("arenaArchiveResults.kapitan")} />
              ) : null}
            </>
          );
          return m.userId ? (
            <Link
              key={m.userId}
              href={`/u/${m.userId}`}
              className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-accent"
            >
              {inner}
            </Link>
          ) : (
            <span key={`${m.displayName}-${i}`} className="flex items-center gap-2 px-1.5 py-1">
              {inner}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function PodiumCard({ title, entries }: { title: string; entries: PodiumEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-amber-400">🏆</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TournamentPodium entries={entries} />
      </CardContent>
    </Card>
  );
}

function RostersCard({
  teams,
  showLobby,
}: {
  teams: TeamCardData[];
  showLobby: boolean;
}) {
  const tUi = useTranslations("Ui");
  if (teams.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          {tUi("arenaArchiveResults.itogovyeSostavy")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <ArenaTeamRosterCard key={team.key} team={team} showLobby={showLobby} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ParticipantsFallback({ tournamentId }: { tournamentId: string }) {
  const tUi = useTranslations("Ui");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{tUi("arenaArchiveResults.uchastniki")}</CardTitle>
      </CardHeader>
      <CardContent>
        <TournamentSoloParticipantsGrid tournamentId={tournamentId} />
      </CardContent>
    </Card>
  );
}

/**
 * Архивный вид Arena-турнира. Два режима составов:
 *  - Random: данные из лидерборда (сумма `arena_round_scores`) — пьедестал,
 *    составы всех троек, таблица баллов по раундам.
 *  - PreMade / игровой путь: данные из серии (`arena_game_placements`) —
 *    пьедестал, составы (из сборки), таблица мест по играм.
 * Если ни того, ни другого нет — фолбэк на список участников.
 */
export function ArenaArchiveResults({
  tournamentId,
}: {
  tournamentId: string;
}) {
  const tUi = useTranslations("Ui");
  const leaderboardQ = useQuery({
    queryKey: ["arena-leaderboard", tournamentId],
    queryFn: () => arenaApi.getLeaderboard(tournamentId),
    enabled: Boolean(tournamentId),
    retry: false,
    staleTime: 30_000,
  });
  const seriesQ = useQuery({
    queryKey: ["arena-series", tournamentId],
    queryFn: () => arenaApi.getSeries(tournamentId),
    enabled: Boolean(tournamentId),
    retry: false,
    staleTime: 30_000,
  });
  const assemblyQ = useQuery({
    queryKey: ["arena-assembly", tournamentId],
    queryFn: () => arenaApi.getAssembly(tournamentId),
    enabled: Boolean(tournamentId),
    retry: false,
    staleTime: 30_000,
  });

  if (leaderboardQ.isLoading || seriesQ.isLoading || assemblyQ.isLoading) {
    return null;
  }

  const lbRows = leaderboardQ.data?.rows ?? [];

  // ── Random: лидерборд с ручными баллами ────────────────────────────────────
  if (lbRows.length > 0) {
    const lb = leaderboardQ.data!;
    const rounds = Array.from({ length: lb.rounds }, (_, i) => i + 1);
    const showLobby = lb.lobbyCount > 1;

    const podium: PodiumEntry[] = lbRows.some((r) => r.totalPoints > 0)
      ? lbRows
          .filter((r) => r.rank <= 3)
          .map((r) => ({
            place: r.rank,
            name: r.name,
            tag: r.tag,
            score: String(r.totalPoints),
            members: r.members.map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
              avatarUrl: m.avatarUrl,
            })),
          }))
      : [];

    const teams: TeamCardData[] = lbRows.map((r) => ({
      key: r.rosterId,
      rosterId: r.rosterId,
      rank: r.rank,
      name: r.name,
      tag: r.tag,
      points: r.totalPoints,
      lobbyNumber: r.lobbyNumber,
      members: r.members,
    }));

    return (
      <div className="space-y-4">
        <PodiumCard title={tUi("arenaArchiveResults.itogiTurnira")} entries={podium} />
        <RostersCard teams={teams} showLobby={showLobby} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              {tUi("arenaArchiveResults.tablitsaBallov")}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-1.5 text-left">#</th>
                  <th className="px-2 py-1.5 text-left">{tUi("arenaArchiveResults.komanda")}</th>
                  {rounds.map((r) => (
                    <th key={r} className="whitespace-nowrap px-2 py-1.5 text-center">
                      Раунд&nbsp;{r}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-right">Σ</th>
                </tr>
              </thead>
              <tbody>
                {lbRows.map((row) => (
                  <tr key={row.rosterId} className="border-t border-border/60">
                    <td className="px-2 py-1.5 font-mono text-muted-foreground">{row.rank}</td>
                    <td className="px-2 py-1.5">
                      <span className="flex items-center gap-2">
                        <Link
                          href={`/rosters/${row.rosterId}`}
                          className="truncate font-medium hover:underline"
                        >
                          {row.name}
                        </Link>
                        <span className="font-mono text-xs text-muted-foreground">{row.tag}</span>
                        {showLobby && (
                          <Badge variant="outline" className="text-[10px]">
                            Л{row.lobbyNumber}
                          </Badge>
                        )}
                      </span>
                    </td>
                    {rounds.map((r) => (
                      <td key={r} className="px-2 py-1.5 text-center tabular-nums text-muted-foreground">
                        {row.pointsByRound[String(r)] ?? 0}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                      {row.totalPoints}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── PreMade / игровой путь: серия с местами по играм ───────────────────────
  const standings = seriesQ.data?.standings ?? [];
  const games = seriesQ.data?.games ?? [];
  const asmTeams = assemblyQ.data?.teams ?? [];

  if (standings.length > 0) {
    // Составы (с игроками) — из сборки; сопоставляем по названию для аватаров подиума.
    const membersByName = new Map<string, TeamMember[]>();
    for (const t of asmTeams) {
      membersByName.set(t.name.trim().toLowerCase(), t.members);
    }

    const podium: PodiumEntry[] = standings
      .filter((s) => s.rank <= 3)
      .map((s) => ({
        place: s.rank,
        name: s.teamName,
        tag: s.teamTag,
        score: String(s.totalPoints),
        members: (membersByName.get(s.teamName.trim().toLowerCase()) ?? []).map((m) => ({
          userId: m.userId,
          displayName: m.displayName,
          avatarUrl: m.avatarUrl,
        })),
      }));

    // Карточки составов: если есть сборка — из неё (с игроками), иначе из standings (без игроков).
    const teams: TeamCardData[] =
      asmTeams.length > 0
        ? asmTeams.map((t) => {
            const st = standings.find(
              (s) => s.teamName.trim().toLowerCase() === t.name.trim().toLowerCase(),
            );
            return {
              key: t.rosterId,
              rosterId: t.rosterId,
              rank: st?.rank,
              name: t.name,
              tag: t.tag,
              points: st?.totalPoints,
              lobbyNumber: t.lobbyNumber,
              members: t.members,
            };
          })
        : standings.map((s) => ({
            key: s.tournamentTeamId,
            rank: s.rank,
            name: s.teamName,
            tag: s.teamTag,
            points: s.totalPoints,
            members: [],
          }));
    // Порядок карточек — по месту.
    teams.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    const hasRosters = asmTeams.length > 0;

    return (
      <div className="space-y-4">
        <PodiumCard title={tUi("arenaArchiveResults.itogiTurnira")} entries={podium} />
        {hasRosters ? <RostersCard teams={teams} showLobby={false} /> : null}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              {tUi("arenaArchiveResults.tablitsaBallov")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ArenaStandingsTable standings={standings} games={games} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Нет данных Arena — показываем хотя бы участников ────────────────────────
  return <ParticipantsFallback tournamentId={tournamentId} />;
}
